import { AddRounded, ContentCopy as ContentCopyIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { Box, Button, Chip, IconButton, Modal, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import API_URLS from '../../backend/ApiUrls'
import { IWorkFlow } from '../../definitions/Workflow'
import Formatter from '../../helpers/Formatter'
import Helper from '../../helpers/Helper'
import WorkflowManager from '../../managers/WorkflowManager'
import { useAppSelector } from '../../store/Hooks'
import SmartCard from '../controls/cards/SmartCard'
import RouteHeader from '../controls/RouteHeader'
import { Cell, Column, Row } from '../controls/table'
import SmartTable from '../controls/table/SmartTable'
import useInvalidate from '../hooks/useInvalidate'
import { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import PermissionsWrapper from '../permissions/PermissionsWrapper'
import RoutingManager from '../routing/Routing'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'
import WorkflowEditor from '../workflow/WorkflowEditor'
import WorkflowModal from '../workflow/WorkflowModal'

interface P {
    contractId?: number
}

export default function Workflows({ contractId }: P) {
    const params = RoutingManager.getParams<{ metadata: { [key: string]: any } }> (useAppSelector(s => s.session.routeParams))
    const invalidate = useInvalidate({ queryKey: buildQueryKey(API_URLS.os.getAllWorkflows + '/*') })
    const [openEditWorkFlowModal, setOpenEditWorkFlowModal] = useState(false)
    const [selectedWorkflow, setSelectedWorkflow] = useState<IWorkFlow>(null)
    const [openWorkflowModal, setOpenWorkflowModal] = useState(false)
    const [openNewWorkFlowModal, setOpenNewWorkFlowModal] = useState(false)

    useEffect(() => {
        if (Helper.hasVal(params?.metadata)) {
            setSelectedWorkflow(params?.metadata?.workflow)
            setOpenWorkflowModal(true)
        }
    }, [params.metadata])

    const columns: Column<IWorkFlow>[] = [
        // @ts-ignore This is to avoid the error of a cyclical data structure comment.reply.reply...
        { id: 'title', label: 'Title' },
        {
            id: 'tasks.title',
            label: 'Your Tasks',
            customRenderer: cell => {
                const tasksCount = Array.isArray(cell.value) ? cell.value.length : 0
                return (
                    <Typography>{tasksCount}</Typography>)
            }
        },
        { id: 'assignee.name', label: 'Assignees' },
        { id: 'createdBy.name', label: 'Created by' },
        { id: 'contract.name', label: 'Contract' },
        {
            id: 'status',
            label: 'Status',
            customRenderer: cell => (
                <Chip
                    label={cell.value === 'open' ? 'Active' : 'Inactive'}
                    style={{ fontWeight: 600 }}
                    color={cell.value === 'open' ? 'primary' : 'default'}
                />
            )
        },
        {
            id: 'dueDate',
            label: 'Due For You',
            customRenderer: (cell, row) => {
                const date = Formatter.timeAgo(new Date(cell.value))
                const status = row.cells[5].value as IWorkFlow['status']
                return (
                    <Chip
                        icon={<CalendarTodayIcon sx={{ fontSize: 'medium' }} />}
                        color={(status === 'open') ? WorkflowManager.getColorFromDate(date.toString()) : 'default'}
                        style={{ fontWeight: 600 }}
                        label={date?.toString()}
                    />
                )
            }
        },
        {
            id: 'uuid',
            label: 'Actions',
            isPK: true,
            customRenderer: (cell, row, item) => (
                <Box display='flex'>
                    <PermissionsWrapper permission='workflows.edit'>
                        <Tooltip title='Edit' arrow>
                            <IconButton onClick={(event: React.MouseEvent) => onEdit(event, item)}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    </PermissionsWrapper>

                    <PermissionsWrapper permission='workflows.create'>
                        <Tooltip title='Clone' arrow>
                            <IconButton onClick={(event: React.MouseEvent) => onClone(event, item)}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </PermissionsWrapper>

                    <PermissionsWrapper permission='workflows.delete'>
                        <Tooltip title='Delete' arrow>
                            <IconButton color='error' onClick={(event: React.MouseEvent) => onDelete(event, cell)}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </PermissionsWrapper>
                </Box>
            )
        }
    ]

    const onClone = async (event: React.MouseEvent, workflow: IWorkFlow) => {
        event.stopPropagation()
        const clonedWorkflow = WorkflowManager.cloneWorkflow(workflow)

        modals.openConfirm({
            title: 'Confirm Workflow Cloning',
            children: <p>Are you sure you want to clone this workflow?</p>,
            labels: { confirm: 'Confirm', cancel: 'Cancel' },
            colors: { confirm: 'success', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    const response = await ApiClient.createWorkflow(clonedWorkflow)
                    if (response && response.uuid) {
                        invalidate()
                        snackbar.show({ severity: 'success', text: `${workflow.title} cloned successfully` })
                    }
                } catch (error) {
                    console.error('Error cloning workflows:', error)
                    snackbar.show({ severity: 'error', text: 'Unable to clone' })
                }
            }
        })
    }

    const handleDelete = async (workflowUUID: string) => {
        modals.openConfirm({
            title: 'Confirm Workflow Deletion',
            children: <p>Are you sure you want to delete this workflow?</p>,
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                try {
                    await ApiClient.deleteWorkflow(workflowUUID)
                    invalidate()
                    snackbar.show({ severity: 'success', text: 'Workflow deleted successfully' })
                } catch (error) {
                    console.error('Problem with deleting workflows:', error)
                    snackbar.show({ severity: 'error', text: 'Unable to delete' })
                }
            }
        })
    }

    const handleOpenWfModal = (indexRow: number, cells: Cell[], row: Row, workflowToOpen: IWorkFlow) => {
        if (workflowToOpen) {
            setSelectedWorkflow(workflowToOpen)
            setOpenWorkflowModal(true)
        } else {
            console.error('Not found')
        }
    }

    const handleOpenEditModal = (workflowToEdit: IWorkFlow) => {
        if (workflowToEdit) {
            setSelectedWorkflow(workflowToEdit)
            setOpenEditWorkFlowModal(true)
        } else {
            console.error('Workflow not found')
        }
    }

    const handleCloseEditModal = () => {
        setOpenEditWorkFlowModal(false)
        setSelectedWorkflow(null)
    }

    const handleCloseWfModal = () => {
        setOpenWorkflowModal(false)
        setSelectedWorkflow(null)
    }

    const onEdit = (event: React.MouseEvent, workflow: IWorkFlow) => {
        event.stopPropagation()
        handleOpenEditModal(workflow)
    }

    const onDelete = (event: React.MouseEvent, cell: Cell) => {
        event.stopPropagation()
        handleDelete(cell.value.toString())
    }

    return (
        <Tile.Container>
            <RouteHeader
                primary='Workflows'
                actions={(
                    <>
                        <Button
                            variant='outlined'>
                            <Typography variant='button'>
                                Team Performance
                            </Typography>
                        </Button>
                        <PermissionsWrapper permission='workflows.create'>
                            <Button
                                variant='contained'
                                onClick={() => setOpenNewWorkFlowModal(true)}
                                startIcon={(<AddRounded />)}>
                                <Typography variant='button'>
                                    New Workflow
                                </Typography>
                            </Button>
                        </PermissionsWrapper>
                    </>
                )}
            />
            <Tile.Column>
                <SmartCard table={true}>
                    <SmartTable<IWorkFlow>
                        toolbar={{
                            columns: true,
                            filters: true,
                            // @ts-ignore This is to avoid the error of a cyclical data structure comment.reply.reply...
                            search: ['title', 'contract.name', 'assignee.name']
                        }}
                        datasource={{
                            remote: {
                                url: API_URLS.os.getAllWorkflows,
                                // noCache: true, //We had put noCache due to the pagination problem with the cache, the problem was that the pagination used lastEvaluatedKey BUT due to some problems when you executed the invalidate it then returned to the wrong page. BUT putting false cache means that the invalidate doesn't work at all and the modified items are not updated upon modification
                                onSuccess: response => {
                                    let workflows: IWorkFlow[] = response.items as IWorkFlow[]
                                    if (response && contractId) {
                                        workflows = workflows.filter(x => x.contract.id === contractId)
                                    }
                                    return { ...response, queryResponse: workflows } as any
                                }
                            }
                        }}
                        noCollapse
                        columns={columns}
                        onClickRow={handleOpenWfModal}
                    />
                </SmartCard>
            </Tile.Column>
            <Modal open={openEditWorkFlowModal} onClose={handleCloseEditModal}>
                <WorkflowEditor
                    workflow={selectedWorkflow}
                    onClose={handleCloseEditModal}
                />
            </Modal>
            <Modal open={openWorkflowModal} onClose={handleCloseWfModal}>
                <WorkflowModal
                    originalWorkflow={selectedWorkflow}
                    onClose={handleCloseWfModal}
                />
            </Modal>
            <Modal open={openNewWorkFlowModal} onClose={() => setOpenNewWorkFlowModal(false)}>
                <WorkflowEditor onClose={() => setOpenNewWorkFlowModal(false)} />
            </Modal>
        </Tile.Container>
    )
}
